import React, { useCallback, useEffect, useState } from "react";
import { Typography, CircularProgress } from "@mui/material";
import { Box } from "@mui/system";
import { useDispatch, useSelector } from "react-redux";
import bs58 from "bs58";
import nacl from "tweetnacl";
import OutlinedButton from "../../Buttons/OutlinedButton";
import { setNotification } from "../../../redux/slices/notifications";
import {
  bind,
  bindChallenge,
  isBound,
  setSelectedWallet,
  setIsLoading,
  WALLET_CONNECTION_TYPES
} from "../../../redux/slices/wallet";
import { setAccountNotification } from "../../../redux/slices/accountNotifications";
import { isWalletBrowser } from "../../../utils/isMobile";
import { useLocation } from "react-router-dom";
import { config } from "../../../config";

function WalletLogin({ closeDialog, wallet }) {
  const { name, hook, connector, Icon } = wallet;
  const dispatch = useDispatch();
  const { isLoading, connectionType } = useSelector((state) => state.wallet);
  const { user } = useSelector((state) => state.auth);
  const [activated, setActivated] = useState(false);
  const [isSolanaActive, setIsSolanaActive] = useState(false);
  const [walletAddress, setWalletAddress] = useState(null);

  const { pathname } = useLocation();
  const [session, setSession] = useState(() => {
    const storedSession = localStorage.getItem("phantom_session");
    return storedSession;
  });
  const [sharedSecret, setSharedSecret] = useState(() => {
    let storedSharedSecret = localStorage.getItem("sharedSecret");
    if (storedSharedSecret) {
      const data = Uint8Array.from(storedSharedSecret.split(",").filter((x) => x));
      return data;
    }
    return "";
  });
  const [challenge, setChallenge] = useState(null);

  const encryptPayload = (payload, sharedSecret) => {
    if (!sharedSecret) throw new Error("missing shared secret");

    const nonce = nacl.randomBytes(24);

    const encryptedPayload = nacl.box.after(
      Buffer.from(JSON.stringify(payload)),
      nonce,
      sharedSecret
    );

    return [nonce, encryptedPayload];
  };

  const decryptPayload = (data, nonce, sharedSecret) => {
    if (!sharedSecret) throw new Error("missing shared secret");

    const decryptedData = nacl.box.open.after(bs58.decode(data), bs58.decode(nonce), sharedSecret);
    if (!decryptedData) {
      throw new Error("Unable to decrypt data");
    }
    return JSON.parse(Buffer.from(decryptedData).toString("utf8"));
  };

  const [dappKeyPair] = useState(() => {
    const storedKeyPair = localStorage.getItem("keyPair");
    if (storedKeyPair) {
      const parsedKeyPair = JSON.parse(storedKeyPair);
      return {
        publicKey: Uint8Array.from(parsedKeyPair.publicKey),
        secretKey: Uint8Array.from(parsedKeyPair.secretKey)
      };
    } else {
      const newKeyPair = nacl.box.keyPair();
      localStorage.setItem(
        "keyPair",
        JSON.stringify({
          publicKey: Object.values(newKeyPair.publicKey),
          secretKey: Object.values(newKeyPair.secretKey)
        })
      );
      return newKeyPair;
    }
  });

  const buildUrl = (path, params) => `phantom://v1/${path}?${params.toString()}`;

  const signMessage = useCallback(
    async (message, address) => {
      try {
        console.log("signMessage", message);
        const { solana } = window;
        if (solana && solana.isPhantom) {
          const encodedMessage = new TextEncoder().encode(message);
          const signedMessage = await solana.signMessage(encodedMessage, "utf8");
          const signatureString = bs58.encode(signedMessage.signature);
          return signatureString;
        } else {
          const payload = {
            session: localStorage.getItem("phantom_session"),
            message: bs58.encode(Buffer.from(message))
          };
          console.log(
            "payload",
            payload,
            session,
            sharedSecret,
            localStorage.getItem("sharedSecret")
          );
          const [nonce, encryptedPayload] = encryptPayload(
            payload,
            Uint8Array.from(
              localStorage
                .getItem("sharedSecret")
                .split(",")
                .filter((x) => x)
            )
          );

          console.log("encryptedPayload nonce", encryptedPayload, nonce);

          const params = new URLSearchParams({
            dapp_encryption_public_key: bs58.encode(dappKeyPair.publicKey),
            cluster: config.SOLANA_NETWORK_PHANTOM,
            app_url: "https://phantom.app",
            payload: bs58.encode(encryptedPayload),
            nonce: bs58.encode(nonce),
            redirect_link: `https://${window.location.host}/settings?linkType=signMessage&public_key=${address}`
          });
          console.log("params", params);
          const url = buildUrl("signMessage", params);

          console.log("url", url);
          window.open(url, "_self");
          return null;
        }
      } catch (error) {
        console.error("Error signing message with Phantom Wallet:", error);
        return null;
      }
    },
    [dappKeyPair.publicKey, session, sharedSecret]
  );

  const onSignature = useCallback(
    async (address, signature) => {
      console.log("onSignature", address, signature);
      dispatch(bind({ walletAddress: address, signature }))
        .then((response) => {
          if (response) {
            closeDialog();
            dispatch(
              setAccountNotification({
                message: "Wallet successfully bound to your account."
              })
            );
            // 清楚url上的查询参数
            window.history.pushState({}, "", pathname);
          }
        })
        .catch((error) => {
          dispatch(setIsLoading(false));
          closeDialog();
          dispatch(setAccountNotification({ code: 4001 }));
        });
    },
    [closeDialog, dispatch, pathname]
  );

  const loginBind = useCallback(
    async (address) => {
      try {
        dispatch(isBound({ walletAddress: address })).then((isBoundRes) => {
          if (isBoundRes) {
            closeDialog();
            dispatch(
              setAccountNotification({
                message: "This wallet is already associated with an existing account."
              })
            );
          } else {
            console.log("start bindChallenge", user?.email, address);
            dispatch(bindChallenge({ email: user?.email, walletAddress: address })).then(
              async (bindChallengeRes) => {
                try {
                  setChallenge(bindChallengeRes.message);
                  console.log("bindChallengeRes", bindChallengeRes);
                  const signature = await signMessage(bindChallengeRes.message, address);
                  console.log("signature", signature);
                  if (signature) {
                    onSignature(address, signature);
                  }
                } catch (error) {
                  console.error("Error binding wallet => :", error);
                }
              }
            );
          }
        });
      } catch (error) {
        console.error("Error connecting to Solana wallet:", error);
      }
    },
    [closeDialog, dispatch, onSignature, signMessage, user?.email]
  );

  useEffect(() => {
    const initialLink = async () => {
      if (window.location.search.includes("linkType=connect")) {
        const urlParams = new URLSearchParams(window.location.search);
        const sharedSecretDapp = nacl.box.before(
          bs58.decode(urlParams.get("phantom_encryption_public_key")),
          dappKeyPair.secretKey
        );

        const decryptedData = decryptPayload(
          urlParams.get("data"),
          urlParams.get("nonce"),
          sharedSecretDapp
        );

        console.log("decryptedData", decryptedData);

        if (decryptedData?.public_key) {
          setWalletAddress(decryptedData.public_key);
          localStorage.setItem("solana_wallet_address", decryptedData.public_key);

          setIsSolanaActive(true);
          console.log("sharedSecretDapp", sharedSecretDapp);
          setSharedSecret(sharedSecretDapp);
          localStorage.setItem("sharedSecret", Object.values(sharedSecretDapp));

          setSession(decryptedData.session);
          localStorage.setItem("phantom_session", decryptedData.session);

          setTimeout(() => {
            loginBind(decryptedData.public_key);
          }, 1000);
        }
      } else if (window.location.search.includes("linkType=signMessage")) {
        const urlParams = new URLSearchParams(window.location.search);
        const signMessageData = decryptPayload(
          urlParams.get("data"),
          urlParams.get("nonce"),
          sharedSecret
        );
        onSignature(urlParams.get("public_key"), signMessageData.signature);
      }
    };
    initialLink();
  }, []);

  const connectPhantomWallet = useCallback(async () => {
    try {
      const { solana } = window;
      if (solana && solana.isPhantom) {
        const response = await solana.connect();
        const address = response.publicKey.toString();
        setWalletAddress(address);
        setIsSolanaActive(true);
        return address;
      } else {
        const params = new URLSearchParams({
          dapp_encryption_public_key: bs58.encode(dappKeyPair.publicKey),
          cluster: config.SOLANA_NETWORK_PHANTOM,
          app_url: "https://phantom.app",
          redirect_link: window.location.href?.includes("?")
            ? `${window.location.href}&linkType=connect`
            : `${window.location.href}?linkType=connect`
        });

        const url = buildUrl("connect", params);
        window.open(url, "_self");
        return null;
      }
    } catch (error) {
      console.error("Error connecting to Phantom Wallet:", error);
    }
  }, [dappKeyPair.publicKey]);

  const login = useCallback(async () => {
    try {
      const address = await connectPhantomWallet();
      if (address) {
        loginBind(address);
      }
    } catch (error) {
      console.error("Error connecting to Solana wallet:", error);
    }
  }, [connectPhantomWallet, loginBind]);

  return (
    <Box my={2} display="flex" justifyContent="space-between" alignItems="center">
      <Box display="flex" flexWrap="nowrap" alignItems="center">
        <Icon />
        <Typography sx={{ ml: 2, textTransform: "capitalize", fontSize: "14px" }}>
          {wallet.name}
        </Typography>
      </Box>

      <OutlinedButton
        disabled={isLoading}
        label={activated ? <CircularProgress color="primary" size={8} /> : "connect"}
        classname="augmented-button-primary"
        color="primary"
        handleClick={() => login()}
        sx={{
          height: "28px",
          width: "75px",
          fontWeight: "normal",
          backgroundImage: "none",
          color: "text.secondary",
          fontSize: "12px"
        }}
      />
    </Box>
  );
}

export default WalletLogin;
